import { CalendarDate, CalendarDateTime, Time } from '@internationalized/date';
import { Temporal } from 'temporal-polyfill';

export type NativeDateInput = string | Temporal.ZonedDateTime;

/**
 * Utilities for converting between Temporal API and @internationalized/date formats.
 * Temporal is used for date/time handling, while @internationalized/date types are required by Nuxt UI components.
 */
function createConverter<TCalendar, TTemporal>(
	toCalendar: (temporal: TTemporal) => TCalendar,
	toTemporal: (calendar: TCalendar) => TTemporal,
) {
	return {
		toCalendar: (value?: TTemporal | null) => (value ? toCalendar(value) : null),
		toTemporal: (value?: TCalendar | null) => (value ? toTemporal(value) : null),
	};
}

export const calendarDateConverter = createConverter<CalendarDate, Temporal.PlainDate>(
	({ year, month, day }) => new CalendarDate(year, month, day),
	({ year, month, day }) => new Temporal.PlainDate(year, month, day),
);

const defaultPlainDateFormatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

/** Locale-aware short date (e.g. en-US uses slashes). */
export const shortPlainDateFormatOptions: Intl.DateTimeFormatOptions = { dateStyle: 'short' };

/**
 * Formats a calendar date with `Intl` for locale-aware output.
 * Pass the active UI locale (for example `useLocale().code`) so formatting follows the app language.
 */
export function formatPlainDate(
	date: Temporal.PlainDate | null | undefined,
	locales?: Intl.LocalesArgument,
	options?: Intl.DateTimeFormatOptions,
): string | undefined {
	if (date == null) return undefined;
	return date.toLocaleString(locales, options ?? defaultPlainDateFormatOptions);
}

export const calendarDateTimeConverter = createConverter<CalendarDateTime, Temporal.PlainDateTime>(
	({ year, month, day, hour, minute, second, millisecond }) =>
		new CalendarDateTime(year, month, day, hour, minute, second, millisecond),
	({ year, month, day, hour, minute, second, millisecond }) =>
		new Temporal.PlainDateTime(year, month, day, hour, minute, second, millisecond),
);

export const timeConverter = createConverter<Time, Temporal.PlainTime>(
	({ hour, minute, second, millisecond }) => new Time(hour, minute, second, millisecond),
	({ hour, minute, second, millisecond }) => new Temporal.PlainTime(hour, minute, second, millisecond),
);

export const unixTimestampConverter = {
	toTemporal: (timestamp: number | null): Temporal.Instant | null => {
		if (timestamp == null) return null;
		return Temporal.Instant.fromEpochMilliseconds(timestamp);
	},
	fromTemporal: (instant: Temporal.Instant | null): number | null => {
		if (instant == null) return null;
		return Number(instant.epochMilliseconds);
	},
	toPlainDateTime: (timestamp: number | null, timeZone = 'UTC'): Temporal.PlainDateTime | null => {
		const instant = unixTimestampConverter.toTemporal(timestamp);
		if (!instant) return null;
		return instant.toZonedDateTimeISO(timeZone).toPlainDateTime();
	},
	fromPlainDateTime: (dateTime: Temporal.PlainDateTime | null, timeZone = 'UTC'): number | null => {
		if (!dateTime) return null;
		const instant = dateTime.toZonedDateTime(timeZone).toInstant();
		return unixTimestampConverter.fromTemporal(instant);
	},
};

export function toNativeDate(date: NativeDateInput): globalThis.Date {
	if (date instanceof Temporal.ZonedDateTime) {
		return new globalThis.Date(Number(date.toInstant().epochMilliseconds));
	}

	return new globalThis.Date(date);
}

export function isPastDateTime(dateTime: Temporal.ZonedDateTime, now = Date.now()): boolean {
	return Number(dateTime.toInstant().epochMilliseconds) <= now;
}

export function formatDateTime(dateTime: Temporal.PlainDateTime): string {
	return dateTime
		.toString({ smallestUnit: 'second' })
		.replace('T', ' ');
}

/** Formats an ISO instant string as `YYYY-MM-DD HH:mm:ss` in the user's local time zone. */
export function formatInstantWithTimeZone(instant: string, timeZone: string = Temporal.Now.timeZoneId()): string {
	return formatDateTime(Temporal.Instant.from(instant).toZonedDateTimeISO(timeZone).toPlainDateTime());
}

export const nowDate = () => Temporal.Now.plainDateISO();

export const nowDateTime = () => Temporal.Now.plainDateTimeISO();

export const nowTime = () => Temporal.Now.plainTimeISO();

// Month bounds (YYYY-MM-DD) for `date`'s month; current month if missing/invalid.
export function getMonthRangeForDate(date?: string): { start: string; end: string } {
	let base: Temporal.PlainDate;

	try {
		base = date ? Temporal.PlainDate.from(date.slice(0, 10)) : nowDate();
	}
	catch {
		base = nowDate();
	}

	return {
		start: base.with({ day: 1 }).toString(),
		end: base.with({ day: base.daysInMonth }).toString(),
	};
}
